import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import {
  MapPin,
  Receipt,
  Store,
  Truck,
  Clock,
  ArrowRight,
  Loader2,
  LogIn,
  LogOut,
  Route as RouteIcon
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';

const Dashboard = () => {
  const { user, api, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, attendanceRes, visitsRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/attendance/today'),
          api.get('/dashboard/recent-visits')
        ]);
        setStats(statsRes.data);
        setAttendance(attendanceRes.data);
        setRecentVisits(visitsRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [api]);

  // Orders and damages are withdrawn (KEYNOTES 3, 4); the day's journey takes their place.
  const statCards = [
    {
      label: "Today's Visits",
      value: stats?.today_visits || 0,
      icon: MapPin,
      color: 'sky',
      link: '/visits'
    },
    {
      label: 'Distance Today',
      value: `${attendance?.distance_km ?? 0} km`,
      icon: RouteIcon,
      color: 'violet',
      link: '/attendance'
    },
    {
      label: 'Travel Type',
      value: attendance?.travel_type || '—',
      icon: Clock,
      color: 'emerald',
      link: '/attendance'
    },
    {
      label: 'Pending Expenses',
      value: stats?.pending_expenses || 0,
      icon: Receipt,
      color: 'amber',
      link: '/expenses'
    },
  ];

  const quickActions = [
    { label: 'New Visit', icon: MapPin, link: '/visits/new', color: 'sky' },
    { label: 'Add Outlet', icon: Store, link: '/outlets/new', color: 'emerald' },
    { label: 'Attendance', icon: Clock, link: '/attendance', color: 'violet' },
    { label: 'Add Expense', icon: Receipt, link: '/expenses/new', color: 'amber' },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-sky-600 to-emerald-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-1">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-white/80 text-sm">
            {isAdmin ? 'Admin Dashboard - Manage your team and operations' : 'Track your visits, orders, and expenses'}
          </p>
        </div>

        {/* Punch reminder - punching in and out is mandatory every working day */}
        {!isAdmin && attendance && !attendance.punched_out && (
          <Link
            to="/attendance"
            data-testid="punch-banner"
            className={cn(
              'flex items-center justify-between gap-3 rounded-2xl border p-4 transition-all',
              attendance.punched_in
                ? 'bg-sky-50 border-sky-200 hover:bg-sky-100'
                : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                attendance.punched_in ? 'bg-sky-100' : 'bg-amber-100'
              )}>
                {attendance.punched_in
                  ? <LogOut className="w-5 h-5 text-sky-600" />
                  : <LogIn className="w-5 h-5 text-amber-600" />}
              </div>
              <div>
                <p className={cn(
                  'font-medium',
                  attendance.punched_in ? 'text-sky-800' : 'text-amber-800'
                )}>
                  {attendance.punched_in ? 'Punch out pending' : 'Punch in to start your day'}
                </p>
                <p className={cn(
                  'text-sm',
                  attendance.punched_in ? 'text-sky-600' : 'text-amber-600'
                )}>
                  {attendance.punched_in
                    ? `${attendance.distance_km} km so far — punch out after reaching home`
                    : 'Punch in from your residence before leaving for the market'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 shrink-0" />
          </Link>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Link
              key={stat.label}
              to={stat.link}
              data-testid={`stat-card-${index}`}
              className="stat-card relative bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all card-hover"
            >
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center mb-3",
                stat.color === 'sky' && "bg-sky-100",
                stat.color === 'emerald' && "bg-emerald-100",
                stat.color === 'violet' && "bg-violet-100",
                stat.color === 'amber' && "bg-amber-100"
              )}>
                <stat.icon className={cn(
                  "w-5 h-5",
                  stat.color === 'sky' && "text-sky-600",
                  stat.color === 'emerald' && "text-emerald-600",
                  stat.color === 'violet' && "text-violet-600",
                  stat.color === 'amber' && "text-amber-600"
                )} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.link}
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                className={cn(
                  "flex items-center gap-3 p-4 rounded-xl border transition-all active:scale-98",
                  action.color === 'sky' && "bg-sky-50 border-sky-200 hover:bg-sky-100",
                  action.color === 'emerald' && "bg-emerald-50 border-emerald-200 hover:bg-emerald-100",
                  action.color === 'violet' && "bg-violet-50 border-violet-200 hover:bg-violet-100",
                  action.color === 'amber' && "bg-amber-50 border-amber-200 hover:bg-amber-100"
                )}
              >
                <action.icon className={cn(
                  "w-5 h-5",
                  action.color === 'sky' && "text-sky-600",
                  action.color === 'emerald' && "text-emerald-600",
                  action.color === 'violet' && "text-violet-600",
                  action.color === 'amber' && "text-amber-600"
                )} />
                <span className={cn(
                  "font-medium text-sm",
                  action.color === 'sky' && "text-sky-700",
                  action.color === 'emerald' && "text-emerald-700",
                  action.color === 'violet' && "text-violet-700",
                  action.color === 'amber' && "text-amber-700"
                )}>{action.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Admin Stats */}
        {isAdmin && (
          <div className="grid grid-cols-2 gap-4">
            <Link 
              to="/distributors" 
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all card-hover"
              data-testid="admin-distributors-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mb-3">
                    <Truck className="w-5 h-5 text-indigo-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_distributors || 0}</p>
                  <p className="text-sm text-slate-500">Distributors</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            </Link>
            <Link 
              to="/outlets" 
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all card-hover"
              data-testid="admin-outlets-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center mb-3">
                    <Store className="w-5 h-5 text-pink-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_outlets || 0}</p>
                  <p className="text-sm text-slate-500">Outlets</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
            </Link>
          </div>
        )}

        {/* Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Visits */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Recent Visits</h3>
              <Link to="/visits" className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                View All
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentVisits.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No visits yet</p>
                </div>
              ) : (
                recentVisits.slice(0, 4).map((visit, idx) => (
                  <div key={visit.id || idx} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {visit.contact_person || 'Visit'}
                          </p>
                          <p className="text-xs text-slate-500">{visit.purpose || 'No purpose specified'}</p>
                        </div>
                      </div>
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        visit.status === 'approved' && "bg-emerald-100 text-emerald-700",
                        visit.status === 'pending' && "bg-amber-100 text-amber-700",
                        visit.status === 'rejected' && "bg-red-100 text-red-700"
                      )}>
                        {visit.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Today's journey */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Today's Journey</h3>
              <Link to="/attendance" className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                View All
              </Link>
            </div>
            {!attendance?.punched_in ? (
              <div className="p-8 text-center text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Not punched in yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                <div className="flex items-center justify-between p-4">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <LogIn className="w-4 h-4 text-emerald-600" /> Punch in
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {new Date(attendance.attendance.punch_in.time)
                      .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <LogOut className={cn('w-4 h-4', attendance.punched_out ? 'text-emerald-600' : 'text-slate-300')} />
                    Punch out
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {attendance.punched_out
                      ? new Date(attendance.attendance.punch_out.time)
                          .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                      : 'Pending'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <RouteIcon className="w-4 h-4 text-violet-600" /> Distance
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {attendance.distance_km} km
                    <span className="text-slate-400">
                      {' '}· {attendance.billable_distance_km} km claimable
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between p-4">
                  <span className="text-sm text-slate-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-sky-600" /> Visits counted
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {attendance.visits_approved} approved / {attendance.visits_total}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
